// src/pages/Vesting.js
import React, { useEffect, useState } from "react";
import { ethers } from "ethers";
import useWallet from "../hooks/useWallet";
import {
  PieChart, Pie, Cell, Tooltip,
  BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Legend, ResponsiveContainer
} from "recharts";
import "../styles/Vesting.css";

const vestingAddress = "0x6fa2D0f273d86157FCE0C7631aac8A2d4f7C4A6a";
const abi = [
  "function getUserClaimData(address,uint8) view returns (uint256,uint256,uint256,uint256,uint256,uint256,uint256)",
  "function vestings(address,uint8) view returns (uint256,uint256,uint256,bool)",
  "function claimTokens(uint8 stage)"
];

const stages = [1, 2, 3];
const COLORS = ["#F97316", "#FACC15"];

export default function Vesting() {
  const { wallet, connectWallet, signer, wcProvider } = useWallet();
  const [vestingData, setVestingData] = useState([]);
  const [totalTokens, setTotalTokens] = useState(0);
  const [unlocked, setUnlocked] = useState(0);
  const [nextUnlockDate, setNextUnlockDate] = useState(null);
  const [loadingClaim, setLoadingClaim] = useState(null);
  const [contract, setContract] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile devices and tablets
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const isMobileOrTablet = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Tablet|Mobile/i.test(userAgent) ||
        (navigator.maxTouchPoints && navigator.maxTouchPoints > 2) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      setIsMobile(isMobileOrTablet);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Get the correct provider based on connection type
  const getProvider = () => {
    if (wcProvider) {
      return new ethers.providers.Web3Provider(wcProvider);
    } else if (window.ethereum) {
      return new ethers.providers.Web3Provider(window.ethereum);
    } else {
      throw new Error("No provider available");
    }
  };

  // Handle wallet connection with mobile-optimized UI (numbered prompt)
  const handleConnectWallet = async () => {
    try {
      console.log("🔍 Connect wallet triggered");
      console.log("Is mobile/tablet:", isMobile);
      console.log("Has ethereum:", !!window.ethereum);
      
      // For mobile/tablet browsers without wallet
      if (isMobile && !window.ethereum) {
        console.log("📱 Mobile/Tablet detected without wallet, showing selection");
        
        const walletOptions = [
          "1️⃣ MetaMask",
          "2️⃣ Trust Wallet", 
          "3️⃣ Rainbow Wallet",
          "4️⃣ Coinbase Wallet",
          "5️⃣ WalletConnect (QR Code)"
        ].join('\n');
        
        const walletChoice = prompt(
          `Select your wallet:\n\n${walletOptions}\n\nEnter number (1-5):`
        );
        
        console.log("User selected:", walletChoice);
        
        switch(walletChoice?.trim()) {
          case '1':
            window.location.href = `https://metamask.app.link/dapp/${window.location.hostname}${window.location.pathname}`;
            break;
          case '2':
            window.location.href = `trust://open_url?coin_id=60&url=${window.location.href}`;
            break;
          case '3':
            window.location.href = `rainbow://open?url=${window.location.href}`;
            break;
          case '4':
            window.location.href = `https://go.cb-w.com/dapp?cb_url=${encodeURIComponent(window.location.href)}`;
            break;
          case '5':
          default:
            console.log("🔄 Initiating WalletConnect");
            const existingModals = document.querySelectorAll('wcm-modal, w3m-modal');
            existingModals.forEach(modal => modal.remove());
            
            await connectWallet();
            
            setTimeout(() => {
              const modals = document.querySelectorAll('wcm-modal, w3m-modal');
              modals.forEach(modal => {
                modal.style.display = 'none';
                modal.remove();
              });
            }, 2000);
            break;
        }
      } else {
        console.log("💻 Standard connection flow");
        await connectWallet();
        
        setTimeout(() => {
          const modals = document.querySelectorAll('wcm-modal, w3m-modal');
          modals.forEach(modal => {
            modal.style.display = 'none';
          });
        }, 2000);
      }
    } catch (error) {
      console.error("Connection error:", error);
      alert("Failed to connect wallet. Please try again.");
    }
  };

  useEffect(() => {
    if (wallet && signer) {
      const _contract = new ethers.Contract(vestingAddress, abi, signer);
      setContract(_contract);
      loadVestingData(_contract, wallet);
    }
  }, [wallet, signer]);

  const loadVestingData = async (_contract, address) => {
    let unlockedTotal = 0;
    let total = 0;
    let nextDate = null;
    const fullData = [];

    for (const stage of stages) {
      try {
        const info = await _contract.getUserClaimData(address, stage);
        const allocated = Number(info[3]) / 1e18;
        const claimed = Number(info[0]) / 1e18;
        const nextUnlock = Number(info[2]);
        const tgeAmount = Number(info[4]) / 1e18;
        const unlockedAmt = Number(info[5]) / 1e18;
        const monthsLeft = Number(info[6]);

        if (allocated > 0) {
          unlockedTotal += claimed;
          total += allocated;

          if (!nextDate || (nextUnlock > 0 && nextUnlock < nextDate)) {
            nextDate = nextUnlock;
          }

          fullData.push({
            stage: `Stage ${stage}`,
            stageId: stage,
            allocated: allocated.toFixed(2),
            claimed: claimed.toFixed(2),
            unlocked: unlockedAmt.toFixed(2),
            tge: tgeAmount.toFixed(2),
            monthsLeft,
            nextUnlock: nextUnlock > 0 ? new Date(nextUnlock * 1000).toLocaleDateString() : "-",
            canClaim: unlockedAmt > claimed
          });
        }
      } catch (err) {
        console.warn("No data for stage", stage, err);
      }
    }

    setUnlocked(unlockedTotal);
    setTotalTokens(total);
    setNextUnlockDate(nextDate ? new Date(nextDate * 1000).toLocaleString() : null);
    setVestingData(fullData);
  };

  const handleClaim = async (stageId) => {
    try {
      setLoadingClaim(stageId);

      const _provider = getProvider();
      const _signer = _provider.getSigner();
      const _contract = new ethers.Contract(vestingAddress, abi, _signer);

      // Network check with retry for mobile
      let network;
      let retries = 3;
      while (retries > 0) {
        try {
          network = await _provider.getNetwork();
          break;
        } catch (error) {
          retries--;
          if (retries === 0) throw error;
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      if (network.chainId !== 1) {
        alert("⚠️ Please switch your wallet to Ethereum Mainnet.");
        setLoadingClaim(null);
        return;
      }

      const tx = await _contract.claimTokens(stageId);
      await tx.wait();
      alert("✅ Tokens claimed successfully!");
      
      // Reload data
      const updatedContract = new ethers.Contract(vestingAddress, abi, _signer);
      await loadVestingData(updatedContract, wallet);
    } catch (err) {
      console.error("Claim failed:", err);
      alert("❌ Claim failed. " + (err.reason || err.message));
    } finally {
      setLoadingClaim(null);
      
      // Clean up modals for mobile
      setTimeout(() => {
        const modals = document.querySelectorAll('wcm-modal, w3m-modal');
        modals.forEach(modal => {
          modal.style.display = 'none';
        });
      }, 1000);
    }
  };

  const pieData = [
    { name: "Unlocked", value: unlocked },
    { name: "Locked", value: totalTokens - unlocked }
  ];

  return (
    <div className="vesting-container">
      <h1>🧬 Vesting Dashboard</h1>
      <p className="vesting-intro">
        MediCoin’s vesting framework ensures transparent, time-based token distribution aligned with investor protection and project sustainability. This dashboard provides a clear view of unlock schedules, claimable balances, and vesting milestones across all presale stages.
      </p>

      {!wallet ? (
        <button className="connect-btn" onClick={handleConnectWallet}>Connect Wallet</button>
      ) : (
        <>
          <div className="vesting-summary">
            <div>
              <h2>Total Vested</h2>
              <p>{totalTokens.toLocaleString(undefined, { maximumFractionDigits: 2 })} MCN</p>
            </div>
            <div>
              <h2>Unlocked</h2>
              <p>{unlocked.toLocaleString(undefined, { maximumFractionDigits: 2 })} MCN</p>
            </div>
            <div>
              <h2>Next Unlock</h2>
              <p>{nextUnlockDate || "-"}</p>
            </div>
          </div>

          <div className="vesting-charts">
            <div>
              <h3>Unlock Distribution</h3>
              <PieChart width={260} height={260}>
                <Pie
                  dataKey="value"
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  labelLine={false}
                  label={({ name, value }) => `${name}: ${value.toLocaleString()}`}
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </div>

            <div>
              <h3>Vesting Duration (Months Left)</h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={vestingData} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="stage" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="monthsLeft" fill="#F43F5E" name="Months Left" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div>
              <h3>TGE Unlock Comparison</h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={vestingData} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="stage" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="tge" fill="#22D3EE" name="TGE Unlocked" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <table className="vesting-table">
            <thead>
              <tr>
                <th>Stage</th>
                <th>Allocated</th>
                <th>Claimed</th>
                <th>Unlocked</th>
                <th>TGE</th>
                <th>Next Unlock</th>
                <th>Claim</th>
              </tr>
            </thead>
            <tbody>
              {vestingData.map((row, idx) => (
                <tr key={idx}>
                  <td>{row.stage}</td>
                  <td>{row.allocated}</td>
                  <td>{row.claimed}</td>
                  <td>{row.unlocked}</td>
                  <td>{row.tge}</td>
                  <td>{row.nextUnlock}</td>
                  <td>
                    <button
                      className="claim-btn"
                      onClick={() => handleClaim(row.stageId)}
                      disabled={loadingClaim === row.stageId}
                    >
                      {loadingClaim === row.stageId ? "Claiming..." : "Claim"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
